// Per-system READABLE address windows — the absolute rcheevos addresses each
// console's shipped read path actually serves. An achievement, scoreSpec, or
// leaderboard gate that touches an address OUTSIDE its system's window reads a
// silent zero at play time (some can never unlock; some become trivially easier
// and false-unlock), so gen-definitions drops them before writing the trigger
// bundles.
//
// Windows are expressed as inclusive [lo, hi] ranges over the SAME address space
// the trigger DSL and scoreSpecs use (rcheevos CPU/region addresses), matching
// routeGbAddress / routeGbaAddress and the readDirect fold in
// src/shared/score-decode.js + src/client/achievements/engine.js.

// This module lives in src/shared so the authoring schema (client and server)
// can use it; scripts/ra-address-windows.mjs re-exports it for gen-definitions.mjs.
export const ADDRESS_WINDOWS = {
  // jsnes exposes the full CPU bus: mirror-folded WRAM ($0000-$1FFF) and the
  // cartridge/expansion bus from $4020 up (PRG-RAM $6000-$7FFF and mapped ROM).
  // $2000-$401F are PPU/APU/IO registers jsnes pins to constants (the Bee 52 /
  // Dragon Quest silent-read class), so they are excluded.
  nes: [[0x0000, 0x1FFF], [0x4020, 0xFFFF]],
  // WRAM only. Cartridge SRAM (rcheevos 0x020000+) is not in the shipped
  // snes9x memory map — those achievements are dropped at generation for now.
  snes: [[0x000000, 0x01FFFF]],
  // WRAM + echo RAM (0xE000-0xFDFF folds into WRAM) + IOAMHRAM (0xFE00-0xFFFF).
  // Cart RAM / VRAM / ROM below 0xC000 are not served.
  gb: [[0xC000, 0xFFFF]],
  // Same as gb plus rcheevos' banked-WRAM window for CGB WRAM banks 2-7.
  gbc: [[0xC000, 0xFFFF], [0x10000, 0x15FFF]],
  // Two console regions mgba exports: IWRAM (rcheevos 0x0000-0x7FFF) and EWRAM
  // (rcheevos 0x8000-0x47FFF). Contiguous in rcheevos address space; kept as two
  // ranges to mirror the routeGbaAddress split.
  gba: [[0x00000, 0x07FFF], [0x08000, 0x47FFF]],
  // 68K work RAM.
  genesis: [[0x0000, 0xFFFF]],
  // 128 bytes of RAM.
  atari2600: [[0x00, 0x7F]],
  // 8MB RDRAM.
  n64: [[0x000000, 0x7FFFFF]],
  // 64 KB of RAM (mem_ram, RAM under ROM and I/O), matching rcheevos.
  c64: [[0x0000, 0xFFFF]],
};

/**
 * Bytes read for every trigger DSL size, matching SIZE_SPECS in
 * src/client/achievements/engine.js (a missing size reads as u8).
 */
export const SIZE_BYTE_WIDTHS = Object.freeze({
  u8: 1, u16_le: 2, u16_be: 2, u24_le: 3, u24_be: 3, u32_le: 4, u32_be: 4,
  bit0: 1, bit1: 1, bit2: 1, bit3: 1, bit4: 1, bit5: 1, bit6: 1, bit7: 1,
  lower4: 1, upper4: 1, bitcount: 1,
});

function widthOf(size) {
  const key = size || 'u8';
  return Object.prototype.hasOwnProperty.call(SIZE_BYTE_WIDTHS, key) ? SIZE_BYTE_WIDTHS[key] : null;
}

/** True when `addr` falls inside any readable window for `systemId` (an own table entry only). */
export function isAddressReadable(systemId, addr) {
  if (typeof systemId !== 'string' || !Object.prototype.hasOwnProperty.call(ADDRESS_WINDOWS, systemId)) return false;
  const ranges = ADDRESS_WINDOWS[systemId];
  if (!ranges || typeof addr !== 'number' || !Number.isFinite(addr)) return false;
  for (const [lo, hi] of ranges) {
    if (addr >= lo && addr <= hi) return true;
  }
  return false;
}

/**
 * Collect every STATIC absolute address a single condition reads, each tagged
 * with a `where` path. Mirrors readConditionValue in engine.js.
 *
 * Pointer (AddAddress) reads resolve their target address at runtime from the
 * pointer's value, so for a ptr-bearing condition ONLY the root hop (ptr[0],
 * read at offset 0) is a static absolute address — the condition's own
 * left/right operands and any later hops are dynamic offsets (typically small
 * struct fields like 0x04) and must NOT be window-checked, or legitimate
 * pointer reads would false-drop.
 */
function collectCondAddrs(c, where, out) {
  if (!c || typeof c !== 'object') return;
  const ptr = Array.isArray(c.ptr) && c.ptr.length ? c.ptr : null;
  if (ptr) {
    if (typeof ptr[0].addr === 'number') out.push({ addr: ptr[0].addr, where: `${where}.ptr[0].addr` });
  } else {
    if (typeof c.addr === 'number') out.push({ addr: c.addr, where: `${where}.addr` });
    if (typeof c.rightAddr === 'number') out.push({ addr: c.rightAddr, where: `${where}.rightAddr` });
  }
  // AddSource/SubSource entries each read a real address into the accumulator;
  // a source may carry its own pointer chain (same root-only rule).
  (c.sources || []).forEach((s, i) => {
    const sPtr = Array.isArray(s.ptr) && s.ptr.length ? s.ptr : null;
    if (sPtr) {
      if (typeof sPtr[0].addr === 'number') out.push({ addr: sPtr[0].addr, where: `${where}.sources[${i}].ptr[0].addr` });
    } else {
      if (typeof s.addr === 'number') out.push({ addr: s.addr, where: `${where}.sources[${i}].addr` });
      if (typeof s.rightAddr === 'number') out.push({ addr: s.rightAddr, where: `${where}.sources[${i}].rightAddr` });
    }
  });
  // Nested condition groups recurse (each nested link may carry its own ptr).
  (c.orGroup || []).forEach((x, i) => collectCondAddrs(x, `${where}.orGroup[${i}]`, out));
  (c.andGroup || []).forEach((x, i) => collectCondAddrs(x, `${where}.andGroup[${i}]`, out));
  (c.joinChain || []).forEach((x, i) => collectCondAddrs(x, `${where}.joinChain[${i}]`, out));
  (c.hitsChain || []).forEach((x, i) => collectCondAddrs(x, `${where}.hitsChain[${i}]`, out));
  if (c.resetNext) collectCondAddrs(c.resetNext, `${where}.resetNext`, out);
}

/** Collect every static absolute address across a trigger's condsets. */
function collectTriggerAddrs(trigger, prefix = '') {
  const out = [];
  if (!trigger) return out;
  const grp = (conds, label) =>
    (conds || []).forEach((c, i) => collectCondAddrs(c, `${prefix}${label}[${i}]`, out));
  grp(trigger.conditions, 'conditions');
  grp(trigger.pauseIf, 'pauseIf');
  grp(trigger.resetIf, 'resetIf');
  (trigger.alts || []).forEach((g, gi) =>
    (g || []).forEach((c, i) => collectCondAddrs(c, `${prefix}alts[${gi}][${i}]`, out)));
  // Alt-group PauseIf/ResetIf (DSL v5) read memory too. gen-definitions.mjs
  // already flattens them into pauseIf/resetIf before calling validateTrigger,
  // so its trigger drop decisions are unchanged. Its validateGate call
  // (gen-definitions.mjs:380) now walks gate altGates as well; every committed
  // leaderboard gate with altGates passes both the old and the new walk.
  (trigger.altGates || []).forEach((g, gi) => {
    if (!g) return;
    grp(g.pauseIf, `altGates[${gi}].pauseIf`);
    grp(g.resetIf, `altGates[${gi}].resetIf`);
  });
  return out;
}

const NESTED_READ_KEYS = ['andGroup', 'orGroup', 'joinChain', 'hitsChain'];

function collectNodeReads(node, path, out) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return;
  const ptr = Array.isArray(node.ptr) && node.ptr.length ? node.ptr : null;
  if (ptr) {
    // Same root-only rule as collectCondAddrs: later hops and the node's own
    // operands are offsets from a runtime pointer value.
    const root = ptr[0];
    if (root && typeof root.addr === 'number') {
      out.push({ path: `${path}.ptr[0].addr`, addr: root.addr, byteWidth: widthOf(root.size) });
    }
  } else {
    if (typeof node.addr === 'number') out.push({ path: `${path}.addr`, addr: node.addr, byteWidth: widthOf(node.size) });
    if (typeof node.rightAddr === 'number') {
      out.push({ path: `${path}.rightAddr`, addr: node.rightAddr, byteWidth: widthOf(node.rightSize) });
    }
  }
  if (Array.isArray(node.sources)) node.sources.forEach((s, i) => collectNodeReads(s, `${path}.sources[${i}]`, out));
  for (const key of NESTED_READ_KEYS) {
    if (Array.isArray(node[key])) node[key].forEach((x, i) => collectNodeReads(x, `${path}.${key}[${i}]`, out));
  }
  if (node.resetNext) collectNodeReads(node.resetNext, `${path}.resetNext`, out);
}

/**
 * Every static address a trigger reads, with the number of bytes read there:
 * the same positions AchievementEngine._sweepReads visits (conditions,
 * pauseIf, resetIf, alts and altGates, and inside each node its sources,
 * andGroup, orGroup, joinChain, hitsChain and resetNext). byteWidth is null
 * for an unknown size. The authoring schema checks each span against the
 * recordable windows (achievement authoring spec 7.2).
 */
export function collectTriggerReads(trigger) {
  const out = [];
  if (!trigger || typeof trigger !== 'object') return out;
  const list = (conds, label) => {
    if (Array.isArray(conds)) conds.forEach((c, i) => collectNodeReads(c, `${label}[${i}]`, out));
  };
  list(trigger.conditions, 'conditions');
  list(trigger.pauseIf, 'pauseIf');
  list(trigger.resetIf, 'resetIf');
  if (Array.isArray(trigger.alts)) trigger.alts.forEach((group, gi) => list(group, `alts[${gi}]`));
  if (Array.isArray(trigger.altGates)) {
    trigger.altGates.forEach((gate, gi) => {
      if (!gate || typeof gate !== 'object') return;
      list(gate.pauseIf, `altGates[${gi}].pauseIf`);
      list(gate.resetIf, `altGates[${gi}].resetIf`);
    });
  }
  return out;
}

/**
 * Validate every address a trigger can touch against `systemId`'s window.
 * Returns { ok, violations: [{ addr, where }] }.
 */
export function validateTrigger(systemId, trigger) {
  const violations = collectTriggerAddrs(trigger)
    .filter(({ addr }) => !isAddressReadable(systemId, addr));
  return { ok: violations.length === 0, violations };
}

/**
 * Validate a scoreSpec against `systemId`'s window. Walks the full read span
 * (addr + stride) for a single-region spec and every term's read for a
 * sum-of-terms spec — the exact bytes readScoreFromRAM touches.
 */
export function validateScoreSpec(systemId, spec) {
  const violations = [];
  if (!spec) return { ok: true, violations };
  if (spec.format === 'terms' && Array.isArray(spec.terms)) {
    spec.terms.forEach((t, i) => {
      const len = t.nibble ? 1 : (t.len || 1);
      for (let b = 0; b < len; b++) {
        const addr = t.addr + b;
        if (!isAddressReadable(systemId, addr)) violations.push({ addr, where: `terms[${i}].addr` });
      }
    });
  } else if (typeof spec.addr === 'number') {
    const len = spec.len || 1;
    const stride = spec.stride && spec.stride > 1 ? spec.stride : 1;
    for (let i = 0; i < len; i++) {
      const addr = spec.addr + i * stride;
      if (!isAddressReadable(systemId, addr)) violations.push({ addr, where: 'addr' });
    }
  }
  return { ok: violations.length === 0, violations };
}

/**
 * Validate a leaderboard active-state gate (start / cancel / submit condsets)
 * against `systemId`'s window. Returns { ok, violations }.
 */
export function validateGate(systemId, gate) {
  const violations = [];
  if (!gate) return { ok: true, violations };
  for (const key of ['start', 'cancel', 'submit']) {
    if (!gate[key]) continue;
    violations.push(...collectTriggerAddrs(gate[key], `${key}.`)
      .filter(({ addr }) => !isAddressReadable(systemId, addr)));
  }
  return { ok: violations.length === 0, violations };
}
